// components/Checkout/PaymentMethods.tsx
import React, { useState, useEffect } from 'react';
import {
  FaCreditCard,
  FaPaypal,
  FaApplePay,
  FaGooglePay,
  FaBuilding,
  FaWallet,
  FaInfoCircle,
  FaCopy,
  FaCheckCircle,
  FaTruck,
  FaCashRegister,
  FaLock,
} from 'react-icons/fa';

// Type definitions
interface BankDetails {
  provider: string;
  accountName: string;
  accountNumber: string;
  reference: string;
  swiftCode?: string;
  qrCode?: string;
  instructions?: string;
  cryptoAddress?: string;
}

interface PaymentProvider {
  id: string;
  name: string;
  category: 'E-Wallet' | 'Crypto' | 'Money Transfer' | 'Bank' | 'Payment Gateway';
  hasQR: boolean;
  details: BankDetails;
}

interface PaymentMethodsProps {
  orderId: string;
  paymentMethod: string;
  onPaymentMethodChange: (method: string) => void;
  showBankDetails: boolean;
  onToggleBankDetails: () => void;
  copied: boolean;
  onCopy: (text: string) => void;
  bankDetails: any;
  formData: any;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBack: () => void;
  onNext: () => void;
}

interface BankTransferDetailsProps {
  orderId?: string;
  onToggleBankDetails: () => void;
  onCopy: (text: string) => void;
  showBankDetails: boolean;
  copied: boolean;
  initialProviderId?: string;
  onProviderChange?: (providerId: string, details: BankDetails) => void;
}

interface CardPaymentDetailsProps {
  formData: any;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

// Sample data with proper typing
const paymentProviders: PaymentProvider[] = [
  // Asian E-Wallets
  { 
    id: 'bmlqr', 
    name: 'BML qr', 
    category: 'Bank',
    hasQR: true,
    details: {
      provider: 'Bank of Maldives',
      accountName: 'VEHIDHI',
      accountNumber: '7770000140688',
      reference: 'TNG-2024-001',
      swiftCode: 'TNGMY',
      qrCode: 'https://storage.vehidhi.com/paymentmethods/IMG_6996.jpeg',
      instructions: 'Scan QR code or use phone number to pay'
    }
  },
  { 
    id: 'tng', 
    name: 'TNG eWallet', 
    category: 'E-Wallet',
    hasQR: true,
    details: {
      provider: 'Touch \'n Go eWallet',
      accountName: 'Mohamed Shaan Ahmed',
      accountNumber: '0123456789',
      reference: 'TNG-2024-001',
      swiftCode: 'TNGMY',
      qrCode: 'https://storage.vehidhi.com/paymentmethods/tngqr.jpg',
      instructions: 'Scan QR code or use phone number to pay'
    }
  },
  { 
    id: 'gopay', 
    name: 'GoPay', 
    category: 'E-Wallet',
    hasQR: true,
    details: {
      provider: 'GoPay Indonesia',
      accountName: 'Your Business Name',
      accountNumber: '081234567890',
      reference: 'GOPAY-2024-001',
      swiftCode: 'GOPAYID',
      qrCode: '/assets/qr/gopay-qr.png',
      instructions: 'Scan QR code with GoPay app'
    }
  },
  { 
    id: 'wechatpay', 
    name: 'WeChat Pay', 
    category: 'E-Wallet',
    hasQR: true,
    details: {
      provider: 'WeChat Pay',
      accountName: 'Your Business Name',
      accountNumber: 'wx1234567890',
      reference: 'WECHAT-2024-001',
      swiftCode: 'WECHATCN',
      qrCode: '/assets/qr/wechat-qr.png',
      instructions: 'Scan QR code with WeChat app'
    }
  },
  { 
    id: 'alipay', 
    name: 'Alipay', 
    category: 'E-Wallet',
    hasQR: true,
    details: {
      provider: 'Alipay',
      accountName: 'Mohamed Shaan Ahmed',
      accountNumber: 'alipay123456',
      reference: 'ALIPAY-2024-001',
      swiftCode: 'ALIPAYCN',
      qrCode: 'https://storage.vehidhi.com/paymentmethods/IMG_7001.jpeg',
      instructions: 'Scan QR code with Alipay app'
    }
  },

  // Cryptocurrency & Digital Payment
  { 
    id: 'binancepay', 
    name: 'Binance Pay', 
    category: 'Crypto',
    hasQR: true,
    details: {
      provider: 'Binance Pay',
      accountName: 'Mohamed Shaan Ahmed',
      accountNumber: '428895808',
      reference: 'BINANCE-2024-001',
      swiftCode: 'BINANCE',
      qrCode: 'https://storage.vehidhi.com/paymentmethods/IMG_7002.png',
      instructions: 'Scan QR code or use Binance ID to pay',
      cryptoAddress: '0x742d35Cc6634C0532925a3b844Bc454e4438f44e'
    }
  },
  { 
    id: 'bybitpay', 
    name: 'Bybit Pay', 
    category: 'Crypto',
    hasQR: true,
    details: {
      provider: 'Bybit Pay',
      accountName: 'Mohamed Shaan Ahmed',
      accountNumber: '73676348',
      reference: 'BYBIT-2024-001',
      swiftCode: 'BYBIT',
      qrCode: 'https://storage.vehidhi.com/paymentmethods/IMG_7003.jpeg',
      instructions: 'Scan QR code with Bybit app',
      cryptoAddress: '0x742d35Cc6634C0532925a3b844Bc454e4438f44e'
    }
  },
  { 
    id: 'redotpay', 
    name: 'RedotPay', 
    category: 'Crypto',
    hasQR: true,
    details: {
      provider: 'RedotPay',
      accountName: 'Mohamed Shaan Ahmed',
      accountNumber: '1977819541',
      reference: 'REDOT-2024-001',
      swiftCode: 'REDOT',
      qrCode: 'https://storage.vehidhi.com/paymentmethods/IMG_7004.jpeg',
      instructions: 'Scan QR code with RedotPay app'
    }
  },

  // Payment & Money Transfer
  { 
    id: 'paypal', 
    name: 'PayPal Transfer', 
    category: 'Money Transfer',
    hasQR: false,
    details: {
      provider: 'PayPal',
      accountName: 'Your Business Name',
      accountNumber: 'yourbusiness@paypal.com',
      reference: 'PAYPAL-2024-001',
      swiftCode: 'PAYPALUS',
      instructions: 'Send to PayPal email address'
    }
  },
  { 
    id: 'wise', 
    name: 'Wise Transfer', 
    category: 'Money Transfer',
    hasQR: false,
    details: {
      provider: 'Wise',
      accountName: 'Your Business Name',
      accountNumber: '1234567890',
      reference: 'WISE-2024-001',
      swiftCode: 'WISEGB',
      instructions: 'Bank transfer via Wise'
    }
  },
  { 
    id: 'revolut', 
    name: 'Revolut', 
    category: 'Money Transfer',
    hasQR: true,
    details: {
      provider: 'Revolut',
      accountName: 'Your Business Name',
      accountNumber: 'GB1234567890',
      reference: 'REVOLUT-2024-001',
      swiftCode: 'REVOGB2L',
      qrCode: '/assets/qr/revolut-qr.png',
      instructions: 'Scan QR code with Revolut app'
    }
  },

  // Southeast Asian Payment
  { 
    id: 'bmlswipe', 
    name: 'BML Swipe', 
    category: 'E-Wallet',
    hasQR: true,
    details: {
      provider: 'BML Swipe',
      accountName: 'Mohamed Shaan Ahmed',
      accountNumber: 'bml901234',
      reference: 'BML-2024-001',
      swiftCode: 'BMLSWIPE',
      qrCode: 'https://storage.vehidhi.com/paymentmethods/Swipe.jpeg',
      instructions: 'Scan QR code with BML Swipe app'
    }
  },
  { 
    id: 'dana', 
    name: 'DANA', 
    category: 'E-Wallet',
    hasQR: true,
    details: {
      provider: 'DANA Indonesia',
      accountName: 'Your Business Name',
      accountNumber: '081298765432',
      reference: 'DANA-2024-001',
      swiftCode: 'DANAID',
      qrCode: '/assets/qr/dana-qr.png',
      instructions: 'Scan QR code with DANA app'
    }
  },
  { 
    id: 'ovo', 
    name: 'OVO', 
    category: 'E-Wallet',
    hasQR: true,
    details: {
      provider: 'OVO Indonesia',
      accountName: 'Your Business Name',
      accountNumber: '081234567890',
      reference: 'OVO-2024-001',
      swiftCode: 'OVOID',
      qrCode: '/assets/qr/ovo-qr.png',
      instructions: 'Scan QR code with OVO app'
    }
  },
  { 
    id: 'grabpay', 
    name: 'GrabPay', 
    category: 'E-Wallet',
    hasQR: true,
    details: {
      provider: 'GrabPay',
      accountName: 'Your Business Name',
      accountNumber: 'grab567890',
      reference: 'GRAB-2024-001',
      swiftCode: 'GRABMY',
      qrCode: '/assets/qr/grab-qr.png',
      instructions: 'Scan QR code with Grab app'
    }
  },
  { 
    id: 'shopee', 
    name: 'ShopeePay', 
    category: 'E-Wallet',
    hasQR: true,
    details: {
      provider: 'ShopeePay',
      accountName: 'Your Business Name',
      accountNumber: 'shopeepay123',
      reference: 'SHOPEE-2024-001',
      swiftCode: 'SHOPEE',
      qrCode: '/assets/qr/shopeepay-qr.png',
      instructions: 'Scan QR code with Shopee app'
    }
  },

  // Traditional Banks
  { 
    id: 'maybank', 
    name: 'Maybank', 
    category: 'Bank',
    hasQR: true,
    details: {
      provider: 'Maybank',
      accountName: 'Your Business Name',
      accountNumber: '123456789012',
      reference: 'MAY-2024-001',
      swiftCode: 'MBBEMYKL',
      qrCode: '/assets/qr/maybank-qr.png',
      instructions: 'Scan QR code or use bank transfer'
    }
  },
  { 
    id: 'cimb', 
    name: 'CIMB Bank', 
    category: 'Bank',
    hasQR: true,
    details: {
      provider: 'CIMB Bank',
      accountName: 'Your Business Name',
      accountNumber: '987654321012',
      reference: 'CIMB-2024-001',
      swiftCode: 'CIBBMYKL',
      qrCode: '/assets/qr/cimb-qr.png',
      instructions: 'Scan QR code with CIMB Clicks app'
    }
  },
  { 
    id: 'publicbank', 
    name: 'Public Bank', 
    category: 'Bank',
    hasQR: false,
    details: {
      provider: 'Public Bank',
      accountName: 'Your Business Name',
      accountNumber: '456789123456',
      reference: 'PB-2024-001',
      swiftCode: 'PBBEMYKL',
      instructions: 'Bank transfer via Public Bank'
    }
  },

  // Other Cryptocurrency
  { 
    id: 'cryptocom', 
    name: 'Crypto.com Pay', 
    category: 'Crypto',
    hasQR: true,
    details: {
      provider: 'Crypto.com Pay',
      accountName: 'Your Business Name',
      accountNumber: 'crypto789012',
      reference: 'CRYPTO-2024-001',
      swiftCode: 'CRYPTO',
      qrCode: '/assets/qr/crypto-com-qr.png',
      instructions: 'Scan QR code with Crypto.com app',
      cryptoAddress: '0x742d35Cc6634C0532925a3b844Bc454e4438f44e'
    }
  },
  { 
    id: 'coingecko', 
    name: 'CoinGecko Pay', 
    category: 'Crypto',
    hasQR: true,
    details: {
      provider: 'CoinGecko Pay',
      accountName: 'Your Business Name',
      accountNumber: 'coingecko456',
      reference: 'CG-2024-001',
      swiftCode: 'COINGECKO',
      qrCode: '/assets/qr/coingecko-qr.png',
      instructions: 'Scan QR code with CoinGecko app',
      cryptoAddress: '0x742d35Cc6634C0532925a3b844Bc454e4438f44e'
    }
  }
];



// Sub-component: Bank Transfer Details
const BankTransferDetails: React.FC<BankTransferDetailsProps> = ({
  orderId,
  onToggleBankDetails,
  onCopy,
  showBankDetails,
  copied,
  initialProviderId = 'tng',
  onProviderChange
}) => {
  const [selectedProvider, setSelectedProvider] = useState<string>(initialProviderId);
  const [bankData, setBankData] = useState<BankDetails | null>(null);
  const [showQR, setShowQR] = useState<boolean>(false);
  const [qrError, setQrError] = useState<boolean>(false);

  // Set initial provider
  useEffect(() => {
    const defaultProvider = paymentProviders.find(p => p.id === initialProviderId);
    if (defaultProvider) {
      setBankData(defaultProvider.details);
    }
  }, [initialProviderId]);

  const handleProviderChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = e.target.value;
    const option = paymentProviders.find(p => p.id === selectedId);
    if (option) {
      setSelectedProvider(selectedId);
      setBankData(option.details);
      setShowQR(false);
      setQrError(false);
      
      if (onProviderChange) {
        onProviderChange(selectedId, option.details);
      }
    }
  };

  const handleShowQR = (): void => {
    setShowQR(!showQR);
    setQrError(false);
  };

  const handleQRError = (): void => {
    setQrError(true);
  };

  const currentProvider = paymentProviders.find(p => p.id === selectedProvider);
  const categories = [...new Set(paymentProviders.map(p => p.category))];

  return (
    <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-4 space-y-3 border border-purple-200">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FaWallet className="text-purple-600 text-xl" />
          <h4 className="font-primary font-bold text-gray-800">Payment Methods</h4>
        </div>
        <button
          type="button"
          onClick={onToggleBankDetails}
          className="text-sm text-purple-600 hover:text-purple-700 font-medium"
        >
          {showBankDetails ? 'Hide Details' : 'Show Details'}
        </button>
      </div>

      {showBankDetails && bankData && (
        <div className="bg-white rounded-lg p-4 space-y-3">
          {/* Provider Dropdown */}
          <div className="space-y-1">
            <label htmlFor="providerSelect" className="text-sm font-medium text-gray-700">
              Select Payment Provider
            </label>
            <select
              id="providerSelect"
              value={selectedProvider}
              onChange={handleProviderChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all duration-200 bg-white"
            >
              {categories.map(category => (
                <optgroup key={category} label={category}>
                  {paymentProviders
                    .filter(p => p.category === category)
                    .map((provider) => (
                      <option key={provider.id} value={provider.id}>
                        {provider.name} {provider.hasQR ? '📱' : '💳'}
                      </option>
                    ))}
                </optgroup>
              ))}
            </select>
          </div>

          {/* Provider Info Badge */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
              {currentProvider?.category}
            </span>
            {currentProvider?.hasQR && (
              <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                QR Available
              </span>
            )}
            {bankData.cryptoAddress && (
              <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                Crypto Supported
              </span>
            )}
          </div>

          {/* QR Code Section */}
          {currentProvider?.hasQR && bankData.qrCode && (
            <div className="space-y-2 border-t border-gray-100 pt-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Scan to Pay</span>
                <button
                  type="button"
                  onClick={handleShowQR}
                  className="text-xs text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1"
                >
                  {showQR ? 'Hide QR Code' : 'Show QR Code'}
                </button>
              </div>
              
              {showQR && (
                <div className="flex flex-col items-center justify-center p-4 bg-gray-50 rounded-lg border border-gray-200">
                  {!qrError ? (
                    <img
                      src={bankData.qrCode}
                      alt={`${bankData.provider} QR Code`}
                      className="w-full h-80 object-contain"
                      onError={handleQRError}
                    />
                  ) : (
                    <div className="text-center py-8">
                      <div className="text-4xl mb-2">📱</div>
                      <p className="text-sm text-gray-600">QR Code not available</p>
                      <p className="text-xs text-gray-400 mt-1">Please use the account details below</p>
                    </div>
                  )}
                  {bankData.instructions && !qrError && (
                    <p className="text-xs text-gray-500 mt-2 text-center">
                      {bankData.instructions}
                    </p>
                  )}
                  {!qrError && (
                    <button
                      type="button"
                      onClick={() => window.open(bankData.qrCode, '_blank')}
                      className="mt-2 text-xs text-purple-600 hover:text-purple-700"
                    >
                      View full size
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Provider Details */}
          <div className={`space-y-2 ${currentProvider?.hasQR ? 'border-t border-gray-100 pt-3' : ''}`}>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Provider:</span>
              <span className="text-sm font-medium text-gray-800">{bankData.provider}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Account Name:</span>
              <span className="text-sm font-medium text-gray-800">{bankData.accountName}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Account Number/ID:</span>
              <div className="flex items-center gap-2">
                <span className="text-sm font-mono font-medium text-gray-800">{bankData.accountNumber}</span>
                <button
                  type="button"
                  onClick={() => onCopy(bankData.accountNumber)}
                  className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                  aria-label="Copy account number"
                >
                  <FaCopy className="text-xs" />
                </button>
              </div>
            </div>
            
            {bankData.cryptoAddress && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Crypto Address:</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-mono font-medium text-gray-800 truncate max-w-[150px]">
                    {bankData.cryptoAddress}
                  </span>
                  <button
                    type="button"
                    onClick={() => onCopy(bankData.cryptoAddress)}
                    className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                    aria-label="Copy crypto address"
                  >
                    <FaCopy className="text-xs" />
                  </button>
                </div>
              </div>
            )}

            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Reference:</span>
              <div className="flex items-center gap-2">
                <span className="text-sm font-mono font-medium text-gray-800">{orderId}</span>
                {/* <span className="text-sm font-mono font-medium text-gray-800">{bankData.reference}</span> */}
                <button
                  type="button"
                  onClick={() => onCopy(bankData.reference)}
                  className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                  aria-label="Copy reference number"
                >
                  <FaCopy className="text-xs" />
                </button>
              </div>
            </div>
            
            {bankData.swiftCode && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">SWIFT/BIC:</span>
                <span className="text-sm font-mono font-medium text-gray-800">{bankData.swiftCode}</span>
              </div>
            )}

            {bankData.instructions && (
              <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs text-blue-700 flex items-center gap-1">
                  <FaInfoCircle className="flex-shrink-0" /> {bankData.instructions}
                </p>
              </div>
            )}

            <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-xs text-yellow-700 flex items-center gap-1">
                <FaInfoCircle className="flex-shrink-0" /> Please use the reference number when making the payment
              </p>
            </div>
            
            {copied && (
              <p className="text-xs text-green-600 flex items-center gap-1">
                <FaCheckCircle className="flex-shrink-0" /> Copied to clipboard!
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Sub-component: COD Payment Details
const CODPaymentDetails: React.FC = ({orderId}) => {
  return (
    <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 space-y-3 border border-amber-200">
      <div className="flex items-center gap-2">
        <FaWallet className="text-amber-600 text-xl" />
        <h4 className="font-primary font-bold text-gray-800">Cash on Delivery</h4>
      </div>
      <div className="space-y-2">
        <p className="font-secondary text-sm text-gray-600">
          Pay when your order arrives at your doorstep. No advance payment required.
        </p>
        <div className="bg-white/80 rounded-lg p-3 space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <FaCheckCircle className="text-green-500 text-xs" />
            <span className="text-gray-700">Pay in cash or card upon delivery</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <FaCheckCircle className="text-green-500 text-xs" />
            <span className="text-gray-700">No additional fees for COD</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <FaCheckCircle className="text-green-500 text-xs" />
            <span className="text-gray-700">Inspect your order before payment</span>
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 p-2 rounded-lg">
          <FaInfoCircle className="text-sm" />
          <span>Please ensure you have the exact change ready for delivery</span>
        </div>
      </div>
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <FaTruck className="text-amber-600" />
        <span>Free delivery for orders over $50</span>
      </div>
    </div>
  );
};

// Sub-component: BML Payment Details
const BMLPaymentDetails: React.FC = ({orderId}) => {
  return (
    <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-4 space-y-3 border border-green-200">
      <div className="flex items-center gap-2">
        <FaBuilding className="text-green-600 text-xl" />
        <h4 className="font-primary font-bold text-gray-800">BML Payment Gateway</h4>
      </div>
      <p className="font-secondary text-sm text-gray-600">
        You will be redirected to the Bank of Maldives payment gateway to complete your secure payment.
      </p>
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <FaLock className="text-green-600" />
        <span>Secured by BML</span>
      </div>
      {import.meta.env.DEV && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2">
          <p className="text-xs text-yellow-700">Development Mode</p>
        </div>
      )}
    </div>
  );
};

// Sub-component: Card Payment Details
const CardPaymentDetails: React.FC<CardPaymentDetailsProps> = ({ formData, onInputChange, orderId }) => {
  return (
    <div className="space-y-4 mt-4">
      <div>
        <label className="block text-sm font-secondary text-gray-600 mb-1">Card Number *</label>
        <input
          type="text"
          name="cardNumber"
          value={formData.cardNumber || ''}
          onChange={onInputChange}
          placeholder="1234 5678 9012 3456"
          required
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200"
        />
      </div>
      <div>
        <label className="block text-sm font-secondary text-gray-600 mb-1">Cardholder Name *</label>
        <input
          type="text"
          name="cardName"
          value={formData.cardName || ''}
          onChange={onInputChange}
          required
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-secondary text-gray-600 mb-1">Expiry Date *</label>
          <input
            type="text"
            name="expiryDate"
            value={formData.expiryDate || ''}
            onChange={onInputChange}
            placeholder="MM/YY"
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200"
          />
        </div>
        <div>
          <label className="block text-sm font-secondary text-gray-600 mb-1">CVV *</label>
          <input
            type="text"
            name="cvv"
            value={formData.cvv || ''}
            onChange={onInputChange}
            placeholder="123"
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200"
          />
        </div>
      </div>
    </div>
  );
};

// Main Component
export function PaymentMethods({
  orderId,
  paymentMethod,
  onPaymentMethodChange,
  showBankDetails,
  onToggleBankDetails,
  copied,
  onCopy,
  bankDetails,
  formData,
  onInputChange,
  onBack,
  onNext,
}: PaymentMethodsProps) {
  const paymentOptions = [
    { id: 'cod', label: 'Cash On Delivery', icon: <FaCashRegister /> },
    { id: 'card', label: 'Credit/Debit Card', icon: <FaCreditCard /> },
    { id: 'bml', label: 'BML Payment', icon: <FaBuilding /> },
    { id: 'bank', label: 'Bank Transfer', icon: <FaWallet /> },
    { id: 'paypal', label: 'PayPal', icon: <FaPaypal /> },
    { id: 'applepay', label: 'Apple Pay', icon: <FaApplePay /> },
    { id: 'googlepay', label: 'Google Pay', icon: <FaGooglePay /> },
  ];

  const renderPaymentDetails = () => {
    switch (paymentMethod) {
      case 'cod':
        return <CODPaymentDetails orderId={orderId}/>;
      case 'bml':
        return <BMLPaymentDetails orderId={orderId}/>;
      case 'bank':
        return (
          <BankTransferDetails
            orderId={orderId}
            onToggleBankDetails={onToggleBankDetails}
            onCopy={onCopy}
            showBankDetails={showBankDetails}
            copied={copied}
            initialProviderId="tng"
          />
        );
      case 'card':
        return <CardPaymentDetails orderId={orderId} formData={formData} onInputChange={onInputChange} />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="font-primary text-lg font-bold text-gray-800">Payment Method</h3>

      {/* Payment Options Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {paymentOptions.map((method) => (
          <button
            key={method.id}
            type="button"
            onClick={() => onPaymentMethodChange(method.id)}
            className={`p-4 border-2 rounded-xl transition-all duration-200 flex flex-col items-center gap-2 ${
              paymentMethod === method.id
                ? 'border-blue-600 bg-blue-50'
                : 'border-gray-200 hover:border-blue-300'
            }`}
          >
            <span className="text-2xl">{method.icon}</span>
            <span className="text-xs font-medium text-gray-700">{method.label}</span>
          </button>
        ))}
      </div>

      {/* Payment Method Details */}
      {renderPaymentDetails()}

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onBack}
          className="flex-1 px-4 py-3 border border-gray-300 hover:bg-gray-50 rounded-xl transition-colors duration-200 font-primary font-semibold"
        >
          Back
        </button>
        <button
          type="button"
          onClick={onNext}
          className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-primary font-semibold py-3 rounded-xl transition-all duration-200"
        >
          Review Order
        </button>
      </div>
    </div>
  );
}